import React from 'react'
import { Box, CircularProgress, Alert } from '@mui/material'
import {
  KpiStrip, Section, ReportTable, fmt, theme,
} from '../../report-shell'

const OrangeOpaskaKPIs = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) return <Alert severity="error">{error}</Alert>
  if (!data) return null

  const ml = data.month_labels || {}

  // ─ Headline KPIs ───────────────────────────────────────────
  const headlineTiles = [
    { key: 'cel',         label: 'Cel',                       value: fmt.int(data.cel || 0),         unit: 'szt.' },
    { key: 'realizacja',  label: `Realizacja (${data.realizacja_pct ?? 0}%)`, value: fmt.int(data.realizacja || 0), unit: 'szt.' },
    { key: 'estymata',    label: `Estymata (${data.estymata_pct ?? 0}%)`,     value: fmt.int(data.estymata || 0),   unit: 'szt.' },
    { key: 'avg_daily',   label: 'Śr. dzienna',               value: fmt.int(data.avg_daily || 0),   unit: 'szt./dzień' },
  ]

  // ─ Trend + monthly tiles ───────────────────────────────────
  const monthlyTiles = [
    { key: 'trend',       label: 'Trend (dziennie do celu)', value: fmt.int(data.trend || 0), unit: 'szt./dzień' },
    { key: 'current',     label: ml.current || 'Bieżący mies.', value: fmt.int(data.current_month_sales || 0) },
    { key: 'prev',        label: ml.prev    || 'Poprzedni mies.', value: fmt.int(data.prev_month_sales || 0) },
    { key: 'prev2',       label: ml.prev2   || '2 mies. temu', value: fmt.int(data.prev2_month_sales || 0) },
    { key: 'total_all',   label: 'Total',                       value: fmt.int(data.total_all_sales || 0) },
  ]

  // ─ KAM table columns ───────────────────────────────────────
  const kamColumns = [
    { field: 'kam', header: 'KAM', width: '110px' },
    { field: 'cel', header: 'Cel', width: '90px', numeric: true },
    { field: 'realizacja', header: 'Realizacja', width: '110px', numeric: true },
    { field: 'realizacja_pct', header: 'Realizacja %', width: '120px',
      percent: { thresholds: [80, 100] },
    },
    { field: 'estymata', header: 'Estymata', width: '110px', numeric: true },
    { field: 'estymata_pct', header: 'Estymata %', width: '120px',
      percent: { thresholds: [80, 100] },
    },
    { field: 'last_day', header: 'Ost. dzień', width: '100px', numeric: true },
    { field: 'current_month', header: 'Bież. mies.', width: '110px', numeric: true },
    { field: 'prev_month', header: 'Poprz. mies.', width: '110px', numeric: true },
    { field: 'older', header: 'Historycznie', width: '110px', numeric: true },
  ]

  const kamRows = (data.kam_kpis || []).map((k, i) => ({ id: k.kam || i, ...k }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <KpiStrip tiles={headlineTiles} />
      <KpiStrip tiles={monthlyTiles} />

      {/* Opaska SOS / Lokalizator breakdown */}
      {data.breakdown && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <BreakdownCard
            title="Opaska SOS"
            current={data.breakdown.opaska_sos.current_month}
            prev={data.breakdown.opaska_sos.prev_month}
            prev2={data.breakdown.opaska_sos.prev2_month}
            ml={ml}
          />
          <BreakdownCard
            title="Lokalizator Orange"
            current={data.breakdown.lokalizator.current_month}
            prev={data.breakdown.lokalizator.prev_month}
            prev2={data.breakdown.lokalizator.prev2_month}
            ml={ml}
          />
        </div>
      )}

      {kamRows.length > 0 && (
        <Section title="KPI per KAM">
          <ReportTable
            rows={kamRows}
            columns={kamColumns}
            initialSort={{ field: 'realizacja', dir: 'desc' }}
            pageSize={25}
            density="comfortable"
          />
        </Section>
      )}
    </div>
  )
}

function BreakdownCard({ title, current, prev, prev2, ml }) {
  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 10, padding: '14px 16px',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: theme.textDim,
        letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8,
      }}>{title}</div>
      <div style={{
        fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
        fontSize: 22, fontWeight: 600, color: theme.text,
        letterSpacing: -1, lineHeight: 1,
      }}>{fmt.int(current || 0)}</div>
      <div style={{ marginTop: 6, fontSize: 11, color: theme.textSub }}>
        {ml.prev || 'Poprz.'}: <strong>{fmt.int(prev || 0)}</strong> ·{' '}
        {ml.prev2 || '2 mies.'}: <strong>{fmt.int(prev2 || 0)}</strong>
      </div>
    </div>
  )
}

export default OrangeOpaskaKPIs
